import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { apiFetch } from '../utils/streamApi';
import { useEntitlements, canAccessMenu } from '../utils/entitlements';
import { DEVICES_PAUSE_CHANGED, announcePauseChange, fmtRemaining } from '../utils/devicesPause';
import useIsMobile from '../utils/useIsMobile';

// A thin strip on every page while devices are paused. A pause can run for
// weeks; without this, an alert that doesn't sound the siren looks like a fault.
// Amber, not SOS red — it is a reminder, not an emergency — and in the page flow
// rather than floating, so a long pause never covers anything.
const POLL_MS = 30000;

export default function DevicesPausedBanner() {
  const [pause, setPause] = useState(null);   // /controls/devices/pause-status body
  const [busy, setBusy]   = useState(false);
  const { pathname }      = useLocation();
  const isMobile          = useIsMobile();
  const entitlements      = useEntitlements();
  // The page is desktop-only; on a phone the Dashboard's Pause Devices tile is the way in.
  const canOpenPage       = !isMobile && canAccessMenu({ menu: 'cameras' }, entitlements);

  const load = useCallback(() => {
    apiFetch('/controls/devices/pause-status')
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d) setPause(d); })
      .catch(() => {});                        // offline — keep what we last knew
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    window.addEventListener('focus', load);
    window.addEventListener(DEVICES_PAUSE_CHANGED, load);
    return () => {
      clearInterval(t);
      window.removeEventListener('focus', load);
      window.removeEventListener(DEVICES_PAUSE_CHANGED, load);
    };
  }, [load]);

  const resume = async () => {
    setBusy(true);
    try {
      await apiFetch('/controls/devices/resume', { method: 'POST' });
      announcePauseChange();
    } finally { setBusy(false); }
  };

  // The Play & Pause Devices page says this itself.
  if (!pause?.paused || pathname === '/devices') return null;

  const text = <>⏸ Devices paused — {fmtRemaining(pause.remaining_secs)}. Alerts won't sound the siren or switch relays.</>;

  return (
    <div role="status" style={{
      display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', flexShrink: 0,
      padding: '7px 16px', fontSize: 13, lineHeight: 1.4,
      background: '#fff8eb', color: '#7a4700', borderBottom: '1px solid #f0d49b',
    }}>
      <span style={{ flex: '1 1 240px' }}>
        {canOpenPage ? <Link to="/devices" style={{ color: 'inherit' }}>{text}</Link> : text}
      </span>
      <button onClick={resume} disabled={busy} style={{
        padding: '6px 14px', minHeight: 32, borderRadius: 8, border: '1px solid #e0b866',
        background: '#ffffff', color: '#7a4700', fontWeight: 700, fontSize: 13, cursor: 'pointer',
      }}>
        {busy ? 'Resuming…' : '▶️ Resume'}
      </button>
    </div>
  );
}
